import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import Menu from '../../panel/Menu';

function ExtraserviceReport() {
    const [selectedDate, setSelectedDate] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchServiceReport = async () => {
        if (!selectedDate) {
            setError('Please select a date first.');
            return;
        }

        setLoading(true);
        setError(null);
        const flattenedServices = [];

        try {
            const bookingsRef = collection(db, 'bookings');
            const q = query(bookingsRef, where('bookingDate', '==', selectedDate));
            const querySnapshot = await getDocs(q);

            querySnapshot.forEach((doc) => {
                const data = doc.data();

                const custName = data.customers?.custname || 'N/A';
                const mobile = data.customers?.mobile || 'N/A';
                const checkin = `${data.checkindate || ''} ${data.checkintime || ''}`.trim() || 'N/A';
                const checkout = `${data.checkoutdate || ''} ${data.checkouttime || ''}`.trim() || 'N/A';

                const roomNumbers = Array.isArray(data.rooms)
                    ? data.rooms.map(r => r.roomname).join(', ')
                    : 'N/A';

                if (Array.isArray(data.services) && data.services.length > 0) {
                    data.services.forEach((service) => {
                        flattenedServices.push({
                            id: `${doc.id}-${service.serviceid || Math.random()}`,
                            customerName: custName,
                            mobile: mobile,
                            roomNo: service.roomname || roomNumbers,
                            serviceName: service.servicename || 'N/A',
                            quantity: Number(service.quantity) || 0,
                            rate: service.servicerate || '0.00',
                            amount: Number(service.servamount) || 0,
                            checkin: checkin,
                            checkout: checkout,
                            addedBy: service.username || 'N/A'
                        });
                    });
                }
            });

            setReportData(flattenedServices);
            if (flattenedServices.length === 0) {
                setError('No extra services found for the selected date.');
            }
        } catch (err) {
            console.error("Error fetching service report: ", err);
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Safely calculate numerical totals
    const totalQty = reportData.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const totalSumAmount = reportData.reduce((sum, item) => sum + (Number(item.rate) || 0), 0);

    return (
        <div>
            <Menu />
            <div className="content-wrapper">
                <section className="content mt-2"></section>
                <div style={styles.container}>
                    {/* Title & Filter Header */}
                    <div style={styles.headerCard}>
                        <h2 style={styles.title}>Extra Service Report</h2>
                        <div style={styles.filterRow}>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Select Booking Date</label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    style={styles.dateInput}
                                />
                            </div>
                            <button
                                onClick={fetchServiceReport}
                                disabled={loading}
                                style={loading ? { ...styles.btn, ...styles.btnDisabled } : styles.btn}
                            >
                                {loading ? 'Fetching...' : 'Fetch Report'}
                            </button>
                        </div>
                    </div>

                    {/* Error / Status Messages */}
                    {error && <div style={styles.errorAlert}>{error}</div>}

                    {/* Report Data Table */}
                    {reportData.length > 0 && (
                        <div style={styles.tableWrapper}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.thRow}>
                                        <th style={styles.th}>Customer Name</th>
                                        <th style={styles.th}>Mobile</th>
                                        <th style={styles.th}>Room No</th>
                                        <th style={styles.th}>Service Name</th>
                                        <th style={styles.th}>Rate</th>
                                        <th style={styles.th}>Qty</th>
                                        <th style={styles.th}>Total Amount</th>
                                        <th style={styles.th}>Check-In Details</th>
                                        <th style={styles.th}>Check-Out Details</th>
                                        <th style={styles.th}>Served By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.map((item, idx) => (
                                        <tr key={item.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: '600' }}>{item.customerName}</td>
                                            <td style={styles.td}>{item.mobile}</td>
                                            <td style={styles.td}><span style={styles.badge}>{item.roomNo}</span></td>
                                            <td style={{ ...styles.td, color: '#1e293b', fontWeight: '500' }}>{item.serviceName}</td>
                                            <td style={styles.td}>₹{item.amount}</td>
                                            <td style={styles.td}>{item.quantity}</td>
                                            <td style={{ ...styles.td, fontWeight: '600', color: '#0f766e' }}>₹{item.rate}</td>
                                            <td style={{ ...styles.td, fontSize: '12px', color: '#64748b' }}>{item.checkin}</td>
                                            <td style={{ ...styles.td, fontSize: '12px', color: '#64748b' }}>{item.checkout}</td>
                                            <td style={styles.td}>{item.addedBy}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={styles.tfRow}>
                                        <td colSpan="5" style={styles.tfLabel}>Grand Total</td>
                                        <td style={styles.tfData}>{totalQty}</td>
                                        <td style={{ ...styles.tfData, color: '#0f766e' }}>₹{Number(totalSumAmount).toFixed(2)}</td>
                                        <td colSpan="3" style={styles.tfData}></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Premium Minimal Styling object
const styles = {
    container: {
        padding: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
    },
    headerCard: {
        background: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
        marginBottom: '24px',
    },
    title: {
        margin: '0 0 16px 0',
        color: '#0f172a',
        fontSize: '22px',
        fontWeight: '700',
    },
    filterRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '16px',
        flexWrap: 'wrap',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569',
    },
    dateInput: {
        padding: '10px 14px',
        borderRadius: '8px',
        border: '1px solid #cbd5e1',
        fontSize: '14px',
        color: '#334155',
        outline: 'none',
        transition: 'border-color 0.2s',
    },
    btn: {
        padding: '11px 22px',
        backgroundColor: '#0f172a',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    btnDisabled: {
        backgroundColor: '#94a3b8',
        cursor: 'not-allowed',
    },
    errorAlert: {
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
        borderRadius: '8px',
        fontSize: '14px',
        marginBottom: '24px',
        border: '1px solid #fee2e2',
    },
    tableWrapper: {
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        textAlign: 'left',
        fontSize: '14px',
    },
    thRow: {
        backgroundColor: '#f1f5f9',
        borderBottom: '2px solid #e2e8f0',
    },
    th: {
        padding: '14px 16px',
        fontWeight: '600',
        color: '#334155',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    td: {
        padding: '14px 16px',
        color: '#475569',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
    },
    trEven: {
        backgroundColor: '#ffffff',
    },
    trOdd: {
        backgroundColor: '#f8fafc',
    },
    tfRow: {
        backgroundColor: '#f1f5f9',
        borderTop: '2px solid #cbd5e1',
    },
    tfLabel: {
        padding: '14px 16px',
        fontWeight: '700',
        color: '#0f172a',
        textAlign: 'right',
    },
    tfData: {
        padding: '14px 16px',
        fontWeight: '700',
        color: '#0f172a',
    },
    badge: {
        backgroundColor: '#e0f2fe',
        color: '#0369a1',
        padding: '4px 8px',
        borderRadius: '6px',
        fontWeight: '600',
        fontSize: '13px',
    },
};

export default ExtraserviceReport;